import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import Hero3D from "../../components/Hero3D.jsx";
import WatchAndShop from "../../components/reels/Reel.jsx";
import Feature from "../../components/Feature";
import { getServiceEnquiryLink } from "../../utils/whatsapp";

/* ================= IMAGE IMPORTS ================= */
import Bg1 from "../../assests/services/L.png";
import Bg2 from "../../assests/services/R.png";

const circleClipStyle = {
  clipPath: "circle(50% at 50% 50%)",
  WebkitMaskImage:
    "radial-gradient(circle at 50% 50%, #000 99%, transparent 100%)",
  maskImage: "radial-gradient(circle at 50% 50%, #000 99%, transparent 100%)",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskSize: "100% 100%",
  maskSize: "100% 100%",
};

const normalizeVideoUrl = (videoUrl, uploadsBase) => {
  if (!videoUrl) return "";
  const value = String(videoUrl).trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("//")) return `https:${value}`;
  return `${uploadsBase}/${value.replace(/^\//, "")}`;
};

const addAutoplayParam = (url) => {
  try {
    const parsed = new URL(url);
    if (!parsed.searchParams.has("autoplay")) parsed.searchParams.set("autoplay", "1");
    return parsed.toString();
  } catch {
    return url;
  }
};

const getYouTubeId = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (parsed.hostname.replace(/^www\./, "") === "youtu.be") {
      return parsed.pathname.replace(/^\//, "");
    }
    if (parsed.pathname.startsWith("/shorts/")) {
      return parsed.pathname.split("/shorts/")[1]?.split("/")[0] || "";
    }
    if (parsed.pathname.startsWith("/live/")) {
      return parsed.pathname.split("/live/")[1]?.split("/")[0] || "";
    }
    if (parsed.searchParams.get("v")) return parsed.searchParams.get("v") || "";
    if (parsed.pathname.startsWith("/embed/")) {
      return parsed.pathname.split("/embed/")[1]?.split("/")[0] || "";
    }
    return "";
  } catch {
    if (String(url).includes("youtu.be/")) return String(url).split("youtu.be/")[1]?.split(/[?&/]/)[0] || "";
    if (String(url).includes("shorts/")) return String(url).split("shorts/")[1]?.split(/[?&/]/)[0] || "";
    if (String(url).includes("live/")) return String(url).split("live/")[1]?.split(/[?&/]/)[0] || "";
    if (String(url).includes("watch?v=")) return String(url).split("watch?v=")[1]?.split("&")[0] || "";
    return "";
  }
};

const buildEmbed = (rawUrl, origin = "") => {
  if (!rawUrl) return null;
  const url = String(rawUrl).trim();
  const lower = url.toLowerCase();
  const isDirectVideo = /\.(mp4|webm|ogg)(\?.*)?$/.test(lower);
  if (isDirectVideo) return { kind: "video", src: url };

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "youtu.be" || host === "youtube.com" || host === "m.youtube.com") {
      const id = getYouTubeId(url);
      if (id) {
        const embedUrl = new URL(`https://www.youtube.com/embed/${id}`);
        embedUrl.searchParams.set("autoplay", "1");
        embedUrl.searchParams.set("mute", "1");
        embedUrl.searchParams.set("playsinline", "1");
        embedUrl.searchParams.set("rel", "0");
        embedUrl.searchParams.set("modestbranding", "1");
        embedUrl.searchParams.set("loop", "1");
        embedUrl.searchParams.set("playlist", id);
        if (origin) embedUrl.searchParams.set("origin", origin);
        return { kind: "iframe", src: embedUrl.toString() };
      }
    }

    if (host === "vimeo.com") {
      const id = parsed.pathname.split("/").filter(Boolean)[0];
      if (id)
        return {
          kind: "iframe",
          src: addAutoplayParam(`https://player.vimeo.com/video/${id}`),
        };
    }

    if (host === "player.vimeo.com")
      return { kind: "iframe", src: addAutoplayParam(url) };
  } catch {
    // ignore parse errors
  }

  // Default: try embedding directly (keeps user on this page)
  return { kind: "iframe", src: url };
};

export default function ServicePage() {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openVideoId, setOpenVideoId] = useState(null);
  const embedOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const sectionRef = useRef(null);
  const itemRefs = useRef([]);
  const imageRefs = useRef([]);
  const textRefs = useRef([]);

  useEffect(() => {
    if (openVideoId == null) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpenVideoId(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openVideoId]);

  useEffect(() => {
    if (openVideoId == null) return;

    const closeVideo = () => setOpenVideoId(null);

    const getActiveCircleEl = () => {
      const activeIndex = services.findIndex(
        (service) => String(service?.id) === String(openVideoId)
      );
      if (activeIndex < 0) return null;
      return imageRefs.current[activeIndex] || null;
    };

    const onScroll = () => closeVideo();

    const onPointerDown = (event) => {
      const activeEl = getActiveCircleEl();
      if (!activeEl) return closeVideo();
      if (!activeEl.contains(event.target)) closeVideo();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("pointerdown", onPointerDown);

    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [openVideoId, services]);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      itemRefs.current.forEach((item, index) => {
        if (!item) return;
        const image = imageRefs.current[index];
        const text = textRefs.current[index];
        const direction = index % 2 === 0 ? 1 : -1;

        if (image) {
          gsap.fromTo(
            image,
            {
              x: 80 * direction,
              rotate: 14 * direction,
              scale: 0.92,
              opacity: 0
            },
            {
              x: 0,
              rotate: 0,
              scale: 1,
              opacity: 1,
              ease: "power3.out",
              scrollTrigger: {
                trigger: item,
                start: "top 80%",
                end: "bottom 60%",
                scrub: true
              }
            }
          );
        }

        if (text) {
          gsap.fromTo(
            text,
            { y: 40, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              ease: "power3.out",
              scrollTrigger: {
                trigger: item,
                start: "top 75%",
                end: "bottom 55%",
                scrub: true
              }
            }
          );
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [services.length]);

  useEffect(() => {
    const controller = new AbortController();
    let requestUrl = "";

    const loadServices = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const backendRoot = process.env.REACT_APP_BACKEND;
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        const baseUrl = (backendUrl || apiBase || backendRoot || "http://localhost:5000").replace(
          /\/$/,
          "",
        );
        const defaultPath = backendUrl ? "/services" : "/api/services";
        const path = process.env.REACT_APP_SERVICES_PATH || defaultPath;
        const params = new URLSearchParams({ status: "active" });
        requestUrl = `${baseUrl}${path}?${params.toString()}`;
        const response = await fetch(requestUrl, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Request failed (${response.status})`);
        }

        const data = await response.json();
        const assetBase = (
          process.env.REACT_APP_ASSET_BASE_URL ||
          backendRoot ||
          "http://localhost:8000"
        ).replace(/\/$/, "");
        const uploadsBase = `${assetBase}/uploads`;
        const dataArray = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        const normalized = dataArray
          .map((item) => ({
            id: item.id,
            title: item.title || "",
            desc: item.description || "",
            img: item.image
              ? item.image.startsWith("http")
                ? item.image
                : `${uploadsBase}/${item.image.replace(/^\//, "")}`
              : "",
            videoUrl: normalizeVideoUrl(
              item.videoUrl ?? item.video_url ?? item.videoURL ?? "",
              uploadsBase,
            ),
          }))
          .filter((item) => item.title && item.desc);

        setServices(normalized);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error fetching services:", err);
          setError(err.message || "Unable to load services");
          setServices([]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadServices();

    return () => controller.abort();
  }, []);

  return (
    <div className="bg-[#f8faff] text-slate-900 overflow-hidden">
      {/* ================= HERO ================= */}
      <Hero3D />

      {/* ================= SERVICES SECTION ================= */}
      <section ref={sectionRef} className="py-10 lg:py-20 bg-[#F8F9FB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
          <div className="text-center mb-16">
           <h2 className="text-3xl sm:text-4xl font-extrabold text-[#351F72] tracking-tight">
            Our Services
          </h2>
          <p className="text-[#000] max-w-2xl mx-auto">
           Expert repair, protection, and recovery solutions with fast turnaround
              and genuine parts.
          </p>
          </div>

          {error && (
            <p className="mb-6 text-sm text-red-500" role="alert">
              {error}
            </p>
          )}

          {isLoading && (
            <p className="mb-6 text-sm text-gray-500">Loading services...</p>
          )}

          <div className="space-y-24">
            {services.map((service, index) => {
              const isReverse = index % 2 !== 0;
              const bgImage = index % 2 === 0 ? Bg1 : Bg2;
              const hasVideo = Boolean(service.videoUrl);
              const isVideoOpen =
                openVideoId != null && String(openVideoId) === String(service.id);
              return (
                <React.Fragment key={service.id || index}>
                  <div
                    ref={(el) => (itemRefs.current[index] = el)}
                    className={`relative overflow-hidden flex flex-col lg:flex-row items-center gap-8 lg:gap-12 ${
                      isReverse ? "lg:flex-row-reverse" : ""
                    }`}
                  >
                    {/* Background Image */}
                    <div
                      className={`absolute inset-0 p-1 pointer-events-none ${
                        index % 2 === 0
                          ? "rounded-l-[120px] sm:rounded-l-[160px] lg:rounded-l-[200px]"
                          : "rounded-r-[120px] sm:rounded-r-[160px] lg:rounded-r-[200px]"
                      }`}
                      style={{
                        backgroundImage: `url(${bgImage})`,
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                      }}
                    />

                    {/* Image */}
                    <div
                      className={`relative w-full lg:w-1/2 flex justify-center p-4 ${
                        isReverse ? "lg:justify-end" : "lg:justify-start"
                      }`}
                    >
                      <div
                        ref={(el) => (imageRefs.current[index] = el)}
                        className="group relative w-full max-w-[260px] sm:max-w-[320px] lg:max-w-[360px] aspect-square rounded-full overflow-hidden shadow-2xl bg-white ring-1 ring-slate-200 transform-gpu isolate"
                        style={circleClipStyle}
                      >
                        {service.img ? (
                          hasVideo ? (
                            <>
                              {!isVideoOpen ? (
                                <button
                                  type="button"
                                  className="relative h-full w-full"
                                  onClick={() => setOpenVideoId(service.id)}
                                  aria-label={`Play video for ${service.title}`}
                                >
                                  <img
                                    src={service.img}
                                    alt={service.title}
                                    className="h-full w-full object-cover"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors duration-200 group-hover:bg-black/30">
                                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-[#351F72] shadow-lg transition-transform duration-200 group-hover:scale-105">
                                      <svg
                                        viewBox="0 0 24 24"
                                        width="28"
                                        height="28"
                                        aria-hidden="true"
                                        focusable="false"
                                      >
                                        <path fill="currentColor" d="M8 5v14l11-7z" />
                                      </svg>
                                    </span>
                                  </div>
                                </button>
                              ) : (
                                <>
                                  {(() => {
                                    const embed = buildEmbed(service.videoUrl, embedOrigin);
                                    if (!embed) return null;
                                    if (embed.kind === "video") {
                                      return (
                                        <video
                                          className="absolute inset-0 h-full w-full object-cover"
                                          controls
                                          autoPlay
                                          playsInline
                                          src={embed.src}
                                        />
                                      );
                                    }
                                    return (
                                      <div
                                        className="absolute inset-0 overflow-hidden"
                                        style={circleClipStyle}
                                      >
                                        <iframe
                                          className="absolute inset-0 h-full w-full border-0"
                                          src={embed.src}
                                          title={
                                            service?.title
                                              ? `Video: ${service.title}`
                                              : "Video"
                                          }
                                          allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                                          allowFullScreen
                                          loading="lazy"
                                          referrerPolicy="strict-origin-when-cross-origin"
                                          style={{
                                            transform: "scale(1.78)",
                                            transformOrigin: "center",
                                          }}
                                        />
                                      </div>
                                    );
                                  })()}
                                </>
                              )}
                            </>
                          ) : (
                            <img
                              src={service.img}
                              alt={service.title}
                              className="w-full h-full object-cover"
                            />
                          )
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-gray-100 text-sm text-gray-500">
                            No image
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    <div
                      ref={(el) => (textRefs.current[index] = el)}
                      className="relative w-full lg:w-1/2 text-left lg:text-left space-y-6 p-4"
                    >
                      <h3 className="text-2xl sm:text-3xl font-bold text-[#351F72]">
                        {service.title}
                      </h3>

                      <p className="text-slate-1000 text-base leading-relaxed max-w-xl mx-auto lg:mx-0">
                        {service.desc}
                      </p>

                      <div className="flex justify-center lg:justify-start">
                        <a
                          href={getServiceEnquiryLink(service)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block bg-[#F3802C] hover:bg-[#F3802C] 
                                     text-white font-medium 
                                     px-6 py-3 rounded-lg 
                                     transition-all duration-300 shadow-md hover:shadow-lg"
                        >
                          Enquiry Now
                        </a>
                      </div>
                    </div>
                  </div>

                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= EXTRA SECTIONS ================= */}
      <Feature />
      <WatchAndShop />
    </div>
  );
}
